import {createContext, useCallback, useContext, useEffect, useRef, useState} from "react";
import {RemoteConstantsModel} from "./models/remoteConstants.model";
import {CommandBase} from "./commands/commandBase";
import {get} from "./utils/http.client";
import {apiBindings} from "./utils/constants";
import {livePreview} from "./utils/livePreview";

interface IntegratorProviderProps {
    children: React.ReactNode;
}

interface IntegratorContextData {
    branches: BranchData[];
    setBranches(value: BranchData[]): void;
    branch: string;
    setBranch(value: string): void;
    applyBranch(value: string): Promise<boolean>;
    state: RemoteConstantsModel;
    totalChanges: number;
    getBranchVersionNumber(branch: string): number;
    forceSetState(state: RemoteConstantsModel): void;
    refreshState(branch: string): Promise<boolean>;
    refreshBranches(): Promise<boolean>;
    // `options.system` bypasses the read-only guard for automatic, non-user
    // normalizations (e.g. the hidden-attribute seed) so read-only viewing still
    // renders correctly. User-initiated edits omit it.
    executeCommand(command: CommandBase, options?: {system?: boolean}): void;
    // Read-only enforcement. app.context sets `readOnly` from the current role
    // (true for Viewer / None); executeCommand no-ops while it's true. The
    // notifier lets app.context surface a toast when an edit is blocked.
    setReadOnly(value: boolean): void;
    setEditBlockedNotifier(notifier: (() => void) | undefined): void;
    clearHistory(): void;
    goBack(): void;
    goForward(): void;
    canGoBack(): boolean;
    canGoForward(): boolean;
}

interface HistoryData {
    frame: number;
    data: string;
    command: CommandBase | null;
}

interface BranchData {
    name: string;
    versionNumber: number;
}

// One-time migration: unify all metadata to uid-based identity.
// - Instance names: localization keyed by uid
// - Description templates: localization keyed by id (shared per type)
// - Entity attribute arrays: store uids instead of ids
// TODO: Remove this once all branches have been loaded and saved.
function migrateToUidBased(state: RemoteConstantsModel) {
    if (!state.localization) return;

    const allAttributes = [
        ...(state.unitAttributes ?? []),
        ...(state.buildingAttributes ?? []),
        ...(state.techActionAttributes ?? []),
        ...(state.techPassiveAttributes ?? []),
        ...(state.rewards ?? []),
    ];

    // Ensure uid-keyed localization exists for instance names
    for (const attr of allAttributes) {
        if (attr.uid === attr.id) continue;
        for (const lang of Object.keys(state.localization)) {
            const loc = state.localization[lang];
            // If uid entry missing but id entry exists, copy id→uid for the name
            if (loc[attr.uid] === undefined && loc[attr.id] !== undefined) {
                loc[attr.uid] = loc[attr.id];
            }
        }
    }

    // Convert unit/building attribute arrays from id to uid
    const attrIdToUid = new Map<string, string>();
    for (const attr of [...(state.unitAttributes ?? []), ...(state.buildingAttributes ?? [])]) {
        // Only map if id is unique (safe to migrate). If duplicates exist, skip (already uid-based or manual fix needed).
        if (!attrIdToUid.has(attr.id)) {
            attrIdToUid.set(attr.id, attr.uid);
        } else {
            attrIdToUid.delete(attr.id); // ambiguous — don't auto-migrate
        }
    }

    for (const unit of (state.units ?? [])) {
        if (!unit.attributes) continue;
        unit.attributes = unit.attributes.map(a => attrIdToUid.get(a) ?? a);
    }
    for (const building of (state.buildings ?? [])) {
        if (!building.attributes) continue;
        building.attributes = building.attributes.map(a => attrIdToUid.get(a) ?? a);
    }
    // Building rewards already use uids — no change needed
}

// One-time migration: convert the flat per-building reward fields
// (collectionTrigger/Type/Requirement, goldPerTurn, populationOnCreation,
// turnsUntilFirstTrigger, amountIncreasePerTrigger, triggerCadence) into
// the new inline `passiveRewards: BuildingReward[]` array. Synthesizes at
// most one reward per building from whatever flat config was present.
// TODO: Remove this once all branches have been loaded and saved.
function migrateBuildingPassiveRewards(state: RemoteConstantsModel) {
    for (const building of (state.buildings ?? [])) {
        const anyB = building as any;
        // Already migrated or freshly created with the new shape.
        if (Array.isArray(building.passiveRewards)) {
            // Still clean up any old fields that may have lingered.
        } else {
            building.passiveRewards = [];
        }

        const trigger = anyB.collectionTrigger as string | undefined;
        const type = anyB.collectionType as string | undefined;
        const requirement = anyB.collectionRequirement as string | undefined;
        const gold = anyB.goldPerTurn as number | undefined;
        const pop = anyB.populationOnCreation as number | undefined;
        const delay = anyB.turnsUntilFirstTrigger as number | undefined;
        const increase = anyB.amountIncreasePerTrigger as number | undefined;
        const cadence = (building.properties as any)?.triggerCadence as number | undefined;

        const grantKind: 'Gold' | 'Population' | undefined =
            type === 'Gold' ? 'Gold' : type === 'Population' ? 'Population' : undefined;
        const amount = grantKind === 'Gold' ? (gold ?? 0) : grantKind === 'Population' ? (pop ?? 0) : 0;

        const shouldSynthesize = building.passiveRewards.length === 0
            && grantKind !== undefined && trigger !== undefined && trigger !== 'None';

        if (shouldSynthesize) {
            let triggerSpec: any;
            if (trigger === 'OnPlace') {
                triggerSpec = {kind: 'OnPlace'};
            } else if (requirement === 'FullTurnOccupied') {
                triggerSpec = {kind: 'FullTurnOccupied'};
            } else if (trigger === 'TilesExplored') {
                triggerSpec = {kind: 'TilesExplored', cloudCount: requirement === 'CloudCount' ? Math.max(1, cadence ?? 1) : 1};
            } else {
                // Legacy TurnStart / TurnEnd trigger names both map to EveryNTurns.
                // Phase has been removed; periodic rewards always fire on turn start.
                triggerSpec = {
                    kind: 'EveryNTurns',
                    cadence: cadence ?? 1,
                    firstTriggerDelay: delay !== undefined && delay >= 0 ? delay : 0,
                };
            }

            building.passiveRewards.push({
                uid: crypto.randomUUID(),
                active: true,
                trigger: triggerSpec,
                grant: {kind: grantKind!, amount},
                amountIncreasePerTrigger: increase ?? 0,
            });
        }

        // Strip flat fields regardless — they no longer exist in the new shape.
        delete anyB.collectionTrigger;
        delete anyB.collectionType;
        delete anyB.collectionRequirement;
        delete anyB.goldPerTurn;
        delete anyB.populationOnCreation;
        delete anyB.turnsUntilFirstTrigger;
        delete anyB.amountIncreasePerTrigger;
        if (building.properties) {
            delete (building.properties as any).triggerCadence;
        }
    }
}

// One-time migration: buildings' rewards are indexed by level, so ensure every
// building has at least an (empty) Level-0 rewards slot — otherwise the editor's
// "skip level 0" rewards rendering can leave the building looking empty.
// Idempotent.
//
// NOTE: image packs are deliberately NOT padded here. The pack views now render
// the first *visible* level via a startLevel-clamped index, so a single-level
// pack shows fine — and cloning pack[0] to pad it would re-create the duplicate
// building levels that the "Tidy image packs" action exists to remove (they'd
// reappear on every reload).
function migrateBuildingLevelZeroSlots(state: RemoteConstantsModel) {
    for (const building of (state.buildings ?? [])) {
        if (!Array.isArray(building.rewards) || building.rewards.length === 0) {
            building.rewards = [[]];
        }
    }
}

// One-time migration: the single player-level `startingGoldPerTurn` was replaced
// by two per-city values — Capital City GPT and (normal) City GPT. The legacy
// value was in practice the capital's income, so it seeds the capital field;
// the normal-city field falls back to the client-side default (1). Idempotent.
// TODO: Remove this once all branches have been loaded and saved.
function migrateCityGoldPerTurn(state: RemoteConstantsModel) {
    const anyState = state as any;
    const legacy = anyState.startingGoldPerTurn as number | undefined;

    state.capitalCityGoldPerTurn ??= legacy ?? 3;
    state.cityGoldPerTurn ??= 1;

    delete anyState.startingGoldPerTurn;
}

// Every load-time normalization above, in order. Anything that holds a fetched
// branch beside the loaded state (Compare Branches) runs it too, so the two
// differ only by real edits, never by what loading itself changed.
export function normalizeLoadedData(data: RemoteConstantsModel) {
    migrateToUidBased(data);
    migrateBuildingPassiveRewards(data);
    migrateBuildingLevelZeroSlots(data);
    migrateCityGoldPerTurn(data);
}

const IntegratorContext = createContext<IntegratorContextData>({} as IntegratorContextData);

export function IntegratorProvider(props: IntegratorProviderProps) {
    const [branches, setBranches] = useState<BranchData[]>([]);
    const [branch, setBranch] = useState<string>(getStoredBranch());
    const [frame, setFrame] = useState<number>(0);
    const [state, setState] = useState<RemoteConstantsModel>({} as RemoteConstantsModel);
    const [history, setHistory] = useState<HistoryData[]>([]);
    const [historyIndex, setHistoryIndex] = useState<number>(0);
    // Read-only mode (driven by role via app.context). When true, user-initiated
    // commands are rejected — a central safety net behind the hidden edit buttons.
    const [readOnly, setReadOnly] = useState<boolean>(false);
    const editBlockedRef = useRef<(() => void) | undefined>(undefined);
    const setEditBlockedNotifier = useCallback((notifier: (() => void) | undefined) => {
        editBlockedRef.current = notifier;
    }, []);

    // Live Theme Preview: every state identity change flows through here —
    // commands (forceSetState), undo/redo (setFromHistory) and branch loads
    // (refreshState, which resets frame to 0 and thereby re-baselines the
    // broadcast session). The publisher no-ops while broadcasting is off.
    useEffect(() => {
        livePreview.onStateChanged(state, frame);
    }, [state, frame]);

    function executeCommand(command: CommandBase, options?: {system?: boolean}): void {
        // Central read-only guard: block every user-initiated data edit for
        // read-only roles (Viewer). `system` commands (automatic normalizations)
        // pass through so read-only viewing still renders correctly.
        if (readOnly && !options?.system) {
            editBlockedRef.current?.();
            return;
        }

        const result = command.execute(state);
        if (!result) {
            return;
        }

        const newFrame = frame + 1;
        setFrame(newFrame);

        if (historyIndex > 0) {
            setHistoryIndex(0);
            history.splice(0, historyIndex);
        }

        history.unshift({
            frame: newFrame,
            data: JSON.stringify(state),
            command: command
        });

        setHistory(history);
        forceSetState(state);
    }

    function goBack() {
        if (!canGoBack()) {
            return;
        }

        setFromHistory(historyIndex + 1);
    }

    function goForward() {
        if (!canGoForward()) {
            return;
        }

        setFromHistory(historyIndex - 1);
    }

    function canGoBack(): boolean {
        return history.length - historyIndex - 1 > 0;
    }

    function canGoForward(): boolean {
        return historyIndex > 0;
    }

    function clearHistory(): void {
        applyHistory(state);
    }

    function setFromHistory(index: number) {
        const historyData = history[index];
        setFrame(historyData.frame);
        setHistoryIndex(index);
        setState(JSON.parse(historyData.data));
    }

    async function refreshState(branch: string): Promise<boolean> {
        const dataResult = await get(`${apiBindings.api}getRemoteConstants?branch=${branch}`, undefined);
        if (!dataResult.success) {
            return false;
        }

        const newData = dataResult.data as RemoteConstantsModel;
        normalizeLoadedData(newData);
        setState(newData);
        applyHistory(newData);

        return true;
    }

    function applyHistory(state: RemoteConstantsModel) {
        setFrame(0);
        setHistoryIndex(0);
        setHistory([{
            frame: 0,
            data: JSON.stringify(state),
            command: null
        }]);
    }

    async function refreshBranches(): Promise<boolean> {
        const dataResult = await get(`${apiBindings.api}getAllBranches`, undefined);
        if (!dataResult.success) {
            return false;
        }

        setBranches(dataResult.data);
        return true;
    }

    function forceSetState(state: RemoteConstantsModel) {
        setState(() => ({
            ...state
        }));
    }

    async function applyBranch(branch: string): Promise<boolean> {
        const result = await refreshState(branch);
        if (!result) {
            return false;
        }

        setBranch(branch);
        storeBranch(branch);
        return true;
    }

    function getBranchVersionNumber(branch: string): number {
        return branches.find(b => b.name === branch)?.versionNumber ?? -1;
    }

    function getStoredBranch(): string {
        return localStorage.getItem('rift-branch') ?? 'Master';
    }

    function storeBranch(branch: string) {
        localStorage.setItem('rift-branch', branch);
    }

    return (
        <IntegratorContext.Provider
            value={{
                branches, setBranches,
                branch, setBranch,
                applyBranch,
                state, forceSetState,
                totalChanges: history.length - historyIndex - 1,
                getBranchVersionNumber,
                refreshState,
                refreshBranches,
                executeCommand,
                setReadOnly,
                setEditBlockedNotifier,
                clearHistory,
                goBack,
                goForward,
                canGoBack,
                canGoForward,
            }}>
            {props.children}
        </IntegratorContext.Provider>
    )
}

export const useIntegrator = () => useContext(IntegratorContext);